/**
 * User Model - Encapsulates user data and provides clean interface for UI components
 * This model handles user data transformation and validation
 */
class UserModel {
  private _serverData: any;

  constructor(serverData: any = null) {
    this._serverData = serverData;
  }

  // Map every field from the API response exactly
  get id() { return this._serverData?.id; }
  get name() { return this._serverData?.name; }
  get email_address() { return this._serverData?.email_address; }
  get phone_number() { return this._serverData?.phone_number; }
  get status() { return this._serverData?.status; }
  get is_super_admin() { return this._serverData?.is_super_admin; }
  get created_at() { return this._serverData?.created_at; }
  get updated_at() { return this._serverData?.updated_at; }
  get role() { return this._serverData?.role; }
  get pin() { return this._serverData?.pin; }

  // Add all other fields as needed from the API response

  // ===== BASIC USER INFORMATION =====
  
  get email() { return this.email_address; }
  
  // ===== ALTERNATIVE FIELD NAMES (for backward compatibility) =====
  
  get emailAddress() { return this.email_address; }
  
  // ===== CALCULATED PROPERTIES =====
  
  get hasValidEmail() { 
    return this.email && this.email.includes('@');
  }
  
  get hasValidPhone() { 
    return this.phone_number && this.phone_number.length >= 10;
  }
  
  get hasValidPin() { 
    return this.pin && this.pin.length === 6;
  }
  
  get isOwner() { 
    return this.role === 'owner';
  }
  
  get isManager() { 
    return this.role === 'manager';
  }
  
  get isOperator() { 
    return this.role === 'operator';
  }
  
  get canManageUsers() { 
    return this.isOwner || this.isManager;
  }
  
  get canManageStore() { 
    return this.isOwner;
  }
  
  // ===== VALIDATION METHODS =====
  
  isValid() {
    return this.id && 
           this.name && 
           this.hasValidEmail && 
           this.hasValidPhone && 
           this.role && 
           this.hasValidPin;
  }
  
  getValidationErrors() {
    const errors = [];
    
    if (!this.id) errors.push('User ID is required');
    if (!this.name) errors.push('Name is required');
    if (!this.hasValidEmail) errors.push('Valid email is required');
    if (!this.hasValidPhone) errors.push('Valid phone number is required');
    if (!this.role) errors.push('Role is required');
    if (!this.hasValidPin) errors.push('6-digit PIN is required');
    
    return errors;
  }
  
  // ===== UPDATE METHODS =====
  
  updateFromServerData(serverData: any) {
    this._serverData = serverData;
  }
  
  // ===== UTILITY METHODS =====
  
  toJSON() {
    return {
      id: this.id,
      name: this.name,
      email: this.email,
      emailAddress: this.emailAddress,
      phoneNumber: this.phone_number,
      role: this.role,
      pin: this.pin,
      hasValidEmail: this.hasValidEmail,
      hasValidPhone: this.hasValidPhone,
      hasValidPin: this.hasValidPin,
      isOwner: this.isOwner,
      isManager: this.isManager,
      isOperator: this.isOperator,
      canManageUsers: this.canManageUsers,
      canManageStore: this.canManageStore
    };
  }
  
  // ===== STATIC METHODS =====
  
  static fromServerData(serverData: any) {
    return new UserModel(serverData);
  }
  
  static createEmpty() {
    return new UserModel();
  }
  
  static createFromFormData(formData: any) {
    return new UserModel({
      name: formData.name,
      email_address: formData.email_address,
      phone_number: formData.phone_number,
      role: formData.role,
      pin: formData.pin,
    });
  }
}

export default UserModel; 